import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import qs from 'qs';
import { setAccessTokenCookie, removeAccessTokenCookie, getAccessTokenCookie } from '@/utils/authCookies';

/**
 * Public api-gateway origin (`VITE_API_URL`) without trailing slashes, e.g. "https://api.example.com".
 */
export function getGatewayUrl(): string {
  return String(import.meta.env.VITE_API_URL ?? '').trim().replace(/\/+$/, '');
}

/** Base config shared by every axios instance that talks to the api-gateway. */
export const gatewayClientConfig = {
  baseURL: `${getGatewayUrl()}/`,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
} as const;

// Create an axios instance with default configurations
const httpClient = axios.create({
  ...gatewayClientConfig,
  paramsSerializer: {
    serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
  }
});

/** Auth endpoints whose 401 means request failure, not an expired access token. */
const PUBLIC_AUTH_PATHS = [
  'auth/api/v1/auth/login',
  'auth/api/v1/auth/register',
  'auth/api/v1/auth/forgot-password',
  'auth/api/v1/auth/reset-password',
  'auth/api/v1/auth/verify-email',
  'auth/api/v1/auth/resend-verification',
  'auth/api/v1/auth/sms/',
  'auth/api/v1/auth/cas/exchange',
  'mcp-crm/api/leads/verify-identity'
] as const;

export function shouldSkipTokenRefresh(url: string): boolean {
  return PUBLIC_AUTH_PATHS.some((path) => url.includes(path));
}

// Separate axios instance for refresh to avoid interceptor recursion
const refreshClient = axios.create(gatewayClientConfig);

/** A request that got a 401 while a refresh was in flight; retried once the refresh settles. */
interface QueuedRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  originalRequest: InternalAxiosRequestConfig;
}

/** True while a single token refresh is in flight. */
let tokenRefreshInProgress = false;
/** Requests waiting for the in-flight refresh. */
let tokenRefreshSubscribers: QueuedRequest[] = [];

/** Same-origin client for the GUI's own Hono server (`/server/api/*`), not the api-gateway. */
export const serverApiClient = axios.create({
  baseURL: '/server/api/',
  withCredentials: true
});

/**
 * Request config that scopes a `/server/api` call to a tenant through the `X-Client-Id` header.
 */
export function withClientIdHeader(clientId: string, config: AxiosRequestConfig = {}): AxiosRequestConfig {
  return { ...config, headers: { ...config.headers, 'X-Client-Id': clientId } };
}

// Request interceptor to add authorization and language headers
httpClient.interceptors.request.use(async (config) => {
  // Add artificial delay
  // await new Promise((resolve) => setTimeout(resolve, 2000));

  // Prefer HttpOnly cookie on server; but if server still expects Authorization, try reading from cookie
  const token = getAccessTokenCookie();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

/**
 * Axios client with Bearer auth from cookie and 401 refresh.
 * On successful refresh, dispatches `auth:changed` so AuthProvider reloads JWT state.
 */
// Response interceptor to handle token refresh
httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = (error.config || {}) as InternalAxiosRequestConfig & { _retry?: boolean };

    const requestUrl: string = originalRequest?.url || '';
    const isRefreshCall = requestUrl.includes('auth/api/v1/auth/refresh');
    const skipRefresh = isRefreshCall || shouldSkipTokenRefresh(requestUrl);

    if (error.response?.status === 401 && !originalRequest._retry && !skipRefresh) {
      originalRequest._retry = true;

      // Queue concurrent 401s while a single refresh is in-flight
      if (tokenRefreshInProgress) {
        return new Promise((resolve, reject) => {
          tokenRefreshSubscribers.push({ resolve, reject, originalRequest });
        });
      }

      try {
        tokenRefreshInProgress = true;

        // Use separate client to avoid interceptor recursion
        const refreshResponse = await refreshClient.post('auth/api/v1/auth/refresh');
        const { accessToken } = refreshResponse.data || {};

        if (!accessToken) {
          throw new Error('No accessToken returned from refresh');
        }
        setAccessTokenCookie(accessToken);

        // Keep React auth state in sync with the refreshed cookie
        window.dispatchEvent(new Event('auth:changed'));

        // Notify any queued requests to retry
        const subscribers = tokenRefreshSubscribers;
        tokenRefreshSubscribers = [];
        subscribers.forEach((sub) => {
          sub.originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          resolveQueuedRequest(sub.resolve, sub.reject, sub.originalRequest);
        });

        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return httpClient(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        removeAccessTokenCookie();
        window.dispatchEvent(new Event('auth:changed'));

        // Fail all queued requests
        const subscribers = tokenRefreshSubscribers;
        tokenRefreshSubscribers = [];
        subscribers.forEach((sub) => sub.reject(refreshError));

        return Promise.reject(error);
      } finally {
        tokenRefreshInProgress = false;
      }
    }

    return Promise.reject(error);
  }
);

/** Retries a queued request with the refreshed token and settles its pending promise. */
function resolveQueuedRequest(
  resolve: QueuedRequest['resolve'],
  reject: QueuedRequest['reject'],
  originalRequest: InternalAxiosRequestConfig
) {
  httpClient(originalRequest).then(resolve).catch(reject);
}

export default httpClient;
